import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import '@/styles/IframeModal.css';
import InfoIcon from '@mui/icons-material/Info'; // Import the Info icon from Material-UI
const Modal = ({ isOpen, onClose, contentType, contentLink, UsedSource, Source, isClickable, SectionIDs }) => {
    const [isModalIframeLoading, setIsModalIframeLoading] = useState(true);
    const formatTimestamp = (timestamp) => {
        const parts = timestamp.split(':');
        return parts.length >= 3 ? `${parts[0]}:${parts[1]}:${parts[2].split('.')[0]}` : timestamp;
    };
    const formatRoles = (roles) => {
        if (!roles || roles.length === 0)
            return 'No roles specified';
        if (roles.length === 1)
            return roles[0];
        if (roles.length === 2)
            return `${roles[0]} and ${roles[1]}`;
        const lastRole = roles.pop();
        return `${roles.join(', ')}, and ${lastRole}`;
    };
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);
    let iframeSrc = contentLink;
    if (contentType === 'mp4' && SectionIDs && SectionIDs.length > 0) {
        iframeSrc = `${contentLink}`; // Modify the URL for video playback
    }
    else if (contentType === 'pdf' || contentType === 'docx') {
        const pageNumber = SectionIDs && SectionIDs.length > 0 ? SectionIDs[0].toString() : '1';
        iframeSrc = `${contentLink}#page=${pageNumber}&zoom=window-width`;
    }
    else if (contentType === 'ppt' || contentType === 'pptx') {
        const slideNumber = SectionIDs && SectionIDs.length > 0 ? SectionIDs[0].toString() : '1';
        iframeSrc = `${contentLink}#slide=id.p${slideNumber}`;
    }
    useEffect(() => {
        if (isOpen) {
            setIsModalIframeLoading(true);
        }
    }, [isOpen, iframeSrc]);
    if (!isOpen)
        return null;
    return ReactDOM.createPortal(_jsx("div", Object.assign({ className: "modal-overlay", onClick: isClickable ? onClose : undefined, role: "dialog", "aria-modal": "true" }, { children: _jsxs("div", Object.assign({ className: "modal-content", onClick: e => e.stopPropagation() }, { children: [_jsx("button", Object.assign({ className: "close-button", onClick: onClose, "aria-label": "Close modal" }, { children: "\u00D7" })), _jsxs("div", Object.assign({ className: "modal-body" }, { children: [isModalIframeLoading && _jsx("div", { className: "iframe-loading-indicator", "aria-live": "polite" }), _jsx("iframe", { src: iframeSrc, title: contentType === 'pdf'
                                ? 'PDF Document'
                                : contentType === 'mp4'
                                    ? 'Video Playback'
                                    : contentType === 'ppt' || contentType === 'pptx'
                                        ? 'PowerPoint Presentation'
                                        : contentType === 'docx'
                                            ? 'Docx Document'
                                            : 'Document', className: "modal-iframe", onLoad: () => setIsModalIframeLoading(false), style: { display: isModalIframeLoading ? 'none' : 'block' }, "aria-label": contentType === 'pdf'
                                ? 'PDF Document'
                                : contentType === 'mp4'
                                    ? 'Video Playback'
                                    : contentType === 'ppt' || contentType === 'pptx'
                                        ? 'PowerPoint Presentation'
                                        : contentType === 'docx'
                                            ? 'Docx Document'
                                            : 'Document' }), (UsedSource || Source) && (_jsxs("div", Object.assign({ className: "source-info" }, { children: [_jsx("h4", { children: (UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.NameWithExt) || (Source === null || Source === void 0 ? void 0 : Source.NameWithExt) }), _jsxs("p", { children: [_jsxs("strong", { children: [' ', Source
                                                    ? 'The relevant information was found on:'
                                                    : "The chatbot's answer was found on:", ' '] }), SectionIDs && SectionIDs.length > 0 ? (_jsxs("span", { children: [contentType === 'pdf'
                                                    ? `${SectionIDs.length === 1 ? 'page' : 'pages'} ${SectionIDs.sort((a, b) => a - b).join(', ')}`
                                                    : null, contentType === 'ppt' || contentType === 'pptx'
                                                    ? `${SectionIDs.length === 1 ? 'slide' : 'slides'} ${SectionIDs.sort((a, b) => a - b).join(', ')}`
                                                    : null, contentType === 'mp4'
                                                    ? `${SectionIDs.length === 1 ? 'timestamp' : 'timestamps'} ${SectionIDs.sort((a, b) => a - b)
                                                        .map(id => formatTimestamp(id.toString()))
                                                        .join(', ')}`
                                                    : null, contentType === 'docx'
                                                    ? `${SectionIDs.length === 1 ? 'page' : 'pages'} ${SectionIDs.sort((a, b) => a - b).join(', ')}`
                                                    : null] })) : null] }), _jsxs("p", { children: [_jsx("strong", { children: "Document Summary:" }), " ", (UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.DocumentSummary) || (Source === null || Source === void 0 ? void 0 : Source.DocumentSummary)] }), _jsxs("p", { children: [_jsx("strong", { children: "Relevant Roles:" }), " ", formatRoles((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.Role) || (Source === null || Source === void 0 ? void 0 : Source.Role))] }), _jsxs("p", { children: [_jsx("strong", { children: "Knowledge Domain:" }), " ", (UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.KnowledgeDomain) || (Source === null || Source === void 0 ? void 0 : Source.KnowledgeDomain)] })] })))] }))] })) })), document.body);
};
const MessageBox = ({ message, position, visible }) => {
    if (!visible)
        return null;
    // Get the viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    // Set a width and height for the message box
    const boxWidth = 200; // Width of the message box
    const boxHeight = 100; // Height of the message box
    let adjustedX = position.x;
    let adjustedY = position.y;
    // Check if the message box goes off the right edge
    if (adjustedX + boxWidth > viewportWidth) {
        adjustedX = viewportWidth - boxWidth - 10; // 10px padding from the right edge
    }
    // Check if the message box goes off the bottom edge
    if (adjustedY + boxHeight > viewportHeight) {
        adjustedY = viewportHeight - boxHeight - 10; // 10px padding from the bottom edge
    }
    return (_jsx("div", Object.assign({ className: "message-box", style: {
            position: 'absolute',
            top: adjustedY,
            left: adjustedX,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: 'black',
            overflowY: 'auto',
            padding: '10px',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            maxWidth: '300px',
            wordWrap: 'break-word'
        } }, { children: message })));
};
const ContentRenderer = ({ UsedSource, Source, onOpen, onUpdateUsedSource }) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const embeddingUrl = (UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.EmbedUrl) || (Source === null || Source === void 0 ? void 0 : Source.EmbedUrl) || ''; // Derived from props so it updates when EmbedUrl arrives
    const [isClickable] = useState(true);
    const [contentType, setContentType] = useState(''); // State for content type
    const [isMiniatureIframeLoading, setIsMiniatureIframeLoading] = useState(true);
    const [tooltipVisible, setTooltipVisible] = useState(false); // State for tooltip visibility
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 }); // Position of the tooltip
    const iframeRef = useRef(null); // Reference to the iframe
    const handleMouseEnter = (event) => {
        // Set the position of the message box based on the miniature's position
        setTooltipPosition({
            x: 80,
            y: 10 // 10px offset from the top of the miniature
        });
        setTooltipVisible(true); // Show message box
    };
    const handleMouseLeave = () => {
        setTooltipVisible(false); // Hide tooltip
    };
    // Function to format timestamp to hh:mm:ss
    const formatTimestamp = (timestamp) => {
        const parts = timestamp.split(':');
        // Ensure we only take the first three parts (hh:mm:ss)
        return parts.length >= 3 ? `${parts[0]}:${parts[1]}:${parts[2].split('.')[0]}` : timestamp;
    };
    // Construct the tooltip message
    const tooltipMessage = (UsedSource || Source) && contentType
        ? `${Source ? 'The relevant information was found on: ' : "The chatbot's answer was found on: "} ${contentType === 'pdf'
            ? `${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || []).length === 1 ? 'page' : 'pages'} ${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || []).sort((a, b) => a - b).join(', ')}`
            : contentType === 'mp4'
                ? `${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || []).length === 1 ? 'timestamp' : 'timestamps'} ${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) ||
                    (Source === null || Source === void 0 ? void 0 : Source.SectionID) ||
                    [])
                    .sort((a, b) => a - b)
                    .map(id => formatTimestamp(id.toString()))
                    .join(', ')}`
                : contentType === 'ppt' || contentType === 'pptx'
                    ? `${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || []).length === 1 ? 'slide' : 'slides'} ${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || []).sort((a, b) => a - b).join(', ')}`
                    : contentType === 'docx'
                        ? `${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || []).length === 1 ? 'page' : 'pages'} ${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || []).sort((a, b) => a - b).join(', ')}`
                        : ''}`
        : '';
    useEffect(() => {
        var _a;
        // Determine content type based on NameWithExt from either UsedSource or Source
        const currentSource = UsedSource || Source; // Use either UsedSource or Source
        if (currentSource) {
            const fileExtension = (_a = currentSource.NameWithExt.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
            if (fileExtension === 'pdf') {
                setContentType('pdf');
            }
            else if (fileExtension === 'ppt' || fileExtension === 'pptx') {
                setContentType('pptx');
            }
            else if (fileExtension === 'mp4') {
                setContentType('mp4');
            }
            else if (fileExtension === 'docx') {
                setContentType('docx');
            }
        }
    }, [UsedSource, Source]); // Add both UsedSource and Source to dependencies
    const handleOpenFullScreen = () => {
        if (iframeRef.current) {
            const iframe = iframeRef.current;
            if (iframe.requestFullscreen) {
                iframe.requestFullscreen();
            }
        }
    };
    const handleOpenModal = () => {
        setModalOpen(true);
        if (onOpen) {
            onOpen();
        }
    };
    // Set heights based on content type for the miniature iframe
    const miniatureIframeWidth = '100%'; // Full width
    const miniatureIframeHeight = contentType === 'pdf' || contentType === 'docx' ? '500px' : '300px'; // 500px for PDFs and DOCX, 300px for others
    // Construct the miniature iframe src
    let miniatureIframeSrc = embeddingUrl; // Use the embeddingUrl directly
    if (contentType === 'pdf') {
        const zoomLevel = 'page-width'; // Set the desired zoom level (e.g., 'page-width', 'page-fit', or '150%')
        miniatureIframeSrc = `${miniatureIframeSrc}#page=${((UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) || [])[0]}&zoom=${zoomLevel}`; // Append the first page number and set zoom
    }
    useEffect(() => {
        setIsMiniatureIframeLoading(true);
    }, [miniatureIframeSrc]);
    // Prevent rendering for .vtt files
    if (contentType === 'vtt') {
        return null; // Or return a placeholder if desired
    }
    return (_jsxs("div", Object.assign({ className: "iframe-item" }, { children: [_jsxs("div", Object.assign({ className: "iframe-miniature", role: "button", tabIndex: 0, style: { position: 'relative', width: miniatureIframeWidth, height: 'auto', overflow: 'hidden' }, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave }, { children: [_jsxs("div", Object.assign({ style: { position: 'relative', width: miniatureIframeWidth, height: miniatureIframeHeight } }, { children: [isMiniatureIframeLoading && _jsx("div", { className: "iframe-loading-indicator", "aria-live": "polite" }), _jsx("iframe", { ref: iframeRef, src: miniatureIframeSrc, title: contentType === 'pdf'
                                    ? 'PDF Document'
                                    : contentType === 'mp4'
                                        ? 'Video Playback'
                                        : contentType === 'docx'
                                            ? 'Docx Document' // Added title for DOCX
                                            : contentType === 'ppt' || contentType === 'pptx'
                                                ? 'PowerPoint Presentation'
                                                : 'Presentation', className: "miniature-iframe", onLoad: () => setIsMiniatureIframeLoading(false), style: {
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                    pointerEvents: 'auto',
                                    overflow: 'hidden',
                                    display: isMiniatureIframeLoading ? 'none' : 'block'
                                }, "aria-label": contentType === 'pdf'
                                    ? 'PDF Document miniature view'
                                    : contentType === 'mp4'
                                        ? 'Video Playback miniature view'
                                        : contentType === 'docx'
                                            ? 'Docx Document miniature view' // Added aria-label for DOCX
                                            : contentType === 'ppt' || contentType === 'pptx'
                                                ? 'PowerPoint Presentation miniature view'
                                                : 'Presentation miniature view' }), _jsx("button", Object.assign({ onClick: handleOpenFullScreen, className: "miniature-button", style: { top: '5px', left: '5px' }, "aria-label": "Open Full Screen", disabled: !isClickable }, { children: "\u26F6" })), _jsx("button", Object.assign({ onClick: handleOpenModal, className: "miniature-button", style: { top: '30px', left: '5px' }, "aria-label": "Open Modal" }, { children: _jsx(InfoIcon, { style: { color: 'inherit', fontSize: '20px' } }) }))] })), _jsx(MessageBox, { message: tooltipMessage, position: tooltipPosition, visible: tooltipVisible })] })), _jsx(Modal, { isOpen: isModalOpen, onClose: () => setModalOpen(false), contentType: contentType, contentLink: embeddingUrl, UsedSource: UsedSource, Source: Source, isClickable: isClickable, SectionIDs: (UsedSource === null || UsedSource === void 0 ? void 0 : UsedSource.SectionID) || (Source === null || Source === void 0 ? void 0 : Source.SectionID) })] })));
};
export default ContentRenderer;
